import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { AvatarModule } from 'primeng/avatar';
import { PageToolbarComponent } from '../../components/page-toolbar/page-toolbar.component';
import { ConferenceService } from '../../services/conference.service';
import {
  BusinessConnectionsLeaderboardDto,
  AttendeeConnectionsLeaderboardDto,
} from '../../dto/conference.dto';

@Component({
  selector: 'app-connections',
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    ProgressSpinnerModule,
    AvatarModule,
    PageToolbarComponent,
  ],
  template: `
    <pb-page-toolbar
      [header]="'Connections Leaderboard'"
      [supportsAdd]="false"
      [supportsEdit]="false"
      [isEditing]="false"
    ></pb-page-toolbar>

    <div class="connections-container">
      <div class="leaderboards-grid">
        <!-- Businesses Leaderboard -->
        <div class="leaderboard-section">
          <h2 class="leaderboard-title">
            <i class="pi pi-building"></i>
            Businesses with Most Connections
          </h2>
          <div *ngIf="loadingBusinesses" class="loading-container">
            <p-progressSpinner></p-progressSpinner>
          </div>
          <div *ngIf="!loadingBusinesses && businesses.length === 0" class="empty-state">
            <p>No business connections data available.</p>
          </div>
          <div *ngIf="!loadingBusinesses && businesses.length > 0" class="leaderboard-list">
            <div
              *ngFor="let business of businesses; let i = index"
              class="leaderboard-item"
              [class.top-three]="i < 3"
            >
              <div class="rank-badge" [class.rank-1]="i === 0" [class.rank-2]="i === 1" [class.rank-3]="i === 2">
                {{ business.rank }}
              </div>
              <div class="item-content">
                <div class="item-header">
                  <div *ngIf="business.logo" class="logo-container">
                    <img [src]="business.logo" [alt]="business.businessName" class="logo" />
                  </div>
                  <div *ngIf="!business.logo" class="logo-placeholder">
                    <i class="pi pi-building"></i>
                  </div>
                  <div class="item-info">
                    <div class="item-name">{{ business.businessName }}</div>
                    <div *ngIf="business.industry" class="item-meta">{{ business.industry }}</div>
                  </div>
                </div>
                <div class="connections-count">
                  <i class="pi pi-link"></i>
                  <span>{{ business.connectionsCount }} connections</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Attendees Leaderboard -->
        <div class="leaderboard-section">
          <h2 class="leaderboard-title">
            <i class="pi pi-users"></i>
            Attendees with Most Connections
          </h2>
          <div *ngIf="loadingAttendees" class="loading-container">
            <p-progressSpinner></p-progressSpinner>
          </div>
          <div *ngIf="!loadingAttendees && attendees.length === 0" class="empty-state">
            <p>No attendee connections data available.</p>
          </div>
          <div *ngIf="!loadingAttendees && attendees.length > 0" class="leaderboard-list">
            <div
              *ngFor="let attendee of attendees; let i = index"
              class="leaderboard-item"
              [class.top-three]="i < 3"
            >
              <div class="rank-badge" [class.rank-1]="i === 0" [class.rank-2]="i === 1" [class.rank-3]="i === 2">
                {{ attendee.rank }}
              </div>
              <div class="item-content">
                <div class="item-header">
                  <p-avatar
                    [image]="attendee.avatar"
                    [label]="attendee.initials || getInitials(attendee.attendeeName)"
                    shape="circle"
                    size="normal"
                  ></p-avatar>
                  <div class="item-info">
                    <div class="item-name">{{ attendee.attendeeName }}</div>
                    <div class="item-meta">
                      <span *ngIf="attendee.title">{{ attendee.title }}</span>
                      <span *ngIf="attendee.title && attendee.company"> • </span>
                      <span *ngIf="attendee.company">{{ attendee.company }}</span>
                    </div>
                  </div>
                </div>
                <div class="connections-count">
                  <i class="pi pi-link"></i>
                  <span>{{ attendee.connectionsCount }} connections</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .connections-container {
        padding: 1rem;
      }

      .leaderboards-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
        gap: 2rem;
        margin-top: 1rem;
      }

      .leaderboard-section {
        background: var(--surface-card);
        border-radius: 8px;
        padding: 1.5rem;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }

      .leaderboard-title {
        font-size: 1.5rem;
        font-weight: 600;
        margin: 0 0 1.5rem 0;
        color: var(--text-color);
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      .leaderboard-title i {
        color: var(--primary-color);
      }

      .loading-container {
        display: flex;
        justify-content: center;
        padding: 2rem;
      }

      .empty-state {
        text-align: center;
        padding: 2rem;
        color: var(--text-color-secondary);
      }

      .leaderboard-list {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .leaderboard-item {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 1rem;
        background: var(--surface-ground);
        border-radius: 8px;
        transition: transform 0.2s, box-shadow 0.2s;
        border: 2px solid transparent;
      }

      .leaderboard-item:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
      }

      .leaderboard-item.top-three {
        background: linear-gradient(135deg, var(--surface-card) 0%, var(--surface-ground) 100%);
      }

      .rank-badge {
        min-width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        font-weight: 700;
        font-size: 1.1rem;
        background: var(--surface-border);
        color: var(--text-color);
      }

      .rank-badge.rank-1 {
        background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
        color: #000;
        box-shadow: 0 2px 8px rgba(255, 215, 0, 0.4);
      }

      .rank-badge.rank-2 {
        background: linear-gradient(135deg, #C0C0C0 0%, #808080 100%);
        color: #000;
        box-shadow: 0 2px 8px rgba(192, 192, 192, 0.4);
      }

      .rank-badge.rank-3 {
        background: linear-gradient(135deg, #CD7F32 0%, #8B4513 100%);
        color: #fff;
        box-shadow: 0 2px 8px rgba(205, 127, 50, 0.4);
      }

      .item-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .item-header {
        display: flex;
        align-items: center;
        gap: 1rem;
      }

      .logo-container {
        width: 50px;
        height: 50px;
        border-radius: 8px;
        overflow: hidden;
        flex-shrink: 0;
      }

      .logo {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .logo-placeholder {
        width: 50px;
        height: 50px;
        border-radius: 8px;
        background: var(--surface-border);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        color: var(--text-color-secondary);
        font-size: 1.5rem;
      }

      .item-info {
        flex: 1;
        min-width: 0;
      }

      .item-name {
        font-weight: 600;
        font-size: 1rem;
        color: var(--text-color);
        margin-bottom: 0.25rem;
      }

      .item-meta {
        font-size: 0.875rem;
        color: var(--text-color-secondary);
      }

      .connections-count {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-weight: 600;
        color: var(--primary-color);
        font-size: 0.95rem;
      }

      .connections-count i {
        font-size: 1rem;
      }

      :host ::ng-deep .p-avatar {
        width: 50px;
        height: 50px;
        flex-shrink: 0;
      }

      @media screen and (max-width: 768px) {
        .leaderboards-grid {
          grid-template-columns: 1fr;
        }

        .connections-container {
          padding: 0.5rem;
        }

        .leaderboard-section {
          padding: 1rem;
        }
      }
    `,
  ],
})
export class ConnectionsComponent implements OnInit {
  businesses: BusinessConnectionsLeaderboardDto[] = [];
  attendees: AttendeeConnectionsLeaderboardDto[] = [];
  loadingBusinesses = true;
  loadingAttendees = true;
  conferenceId: string = '';

  constructor(
    private conferenceService: ConferenceService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.conferenceId = this.route.snapshot.params['conferenceId'] || '';
    this.loadLeaderboards();
  }

  loadLeaderboards(): void {
    // Load businesses leaderboard
    this.loadingBusinesses = true;
    this.conferenceService.getBusinessConnectionsLeaderboard(this.conferenceId).subscribe({
      next: (data) => {
        this.businesses = data;
        this.loadingBusinesses = false;
      },
      error: (error) => {
        console.error('Error loading businesses leaderboard:', error);
        this.loadingBusinesses = false;
      },
    });

    // Load attendees leaderboard
    this.loadingAttendees = true;
    this.conferenceService.getAttendeeConnectionsLeaderboard(this.conferenceId).subscribe({
      next: (data) => {
        this.attendees = data;
        this.loadingAttendees = false;
      },
      error: (error) => {
        console.error('Error loading attendees leaderboard:', error);
        this.loadingAttendees = false;
      },
    });
  }

  getInitials(name: string): string {
    if (!name) return '';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }
}

